import { v4 as uuidv4 } from 'uuid'
import _cloneDeep from 'lodash.clonedeep'
import { MAX_CONDITION_GROUP_DEPTH } from '../../../../common/constants'
import type { Pattern } from '../../contexts/PatternContext'
import { migrateLegacyChangedConditions } from '../../utils/migrateLegacyChangedConditions'
import { IndicatorLookup, normalizeConditionValidity } from '../../utils/normalizeConditionValidity'
import { resolveSideIndicator } from '../../../../common/utils/composite/registryEntry'

export type NormalizeDraftResult =
  | { ok: true; pattern: Pattern; warnings: string[] }
  | { ok: false; reason: string }

const MAX_DRAFT_NODES = 100

const TRIGGER_TYPES = new Set(['atPost', 'anytime', 'timeBeforePost'])

const isValidChartDetails = (cd: any): boolean =>
  !!cd &&
  typeof cd === 'object' &&
  typeof cd.symbol === 'string' &&
  !!cd.periodicity &&
  typeof cd.timeframe === 'string'

const isValidTrigger = (trigger: any): boolean =>
  !!trigger &&
  typeof trigger === 'object' &&
  TRIGGER_TYPES.has(trigger.type) &&
  isValidChartDetails(trigger.chartDetails)

const countNodes = (groups: any[]): number => {
  let count = 0
  const walk = (group: any) => {
    if (!group || typeof group !== 'object') return
    count += 1 + (Array.isArray(group.conditions) ? group.conditions.length : 0)
    ;(Array.isArray(group.conditionGroups) ? group.conditionGroups : []).forEach(walk)
  }
  groups.forEach(walk)
  return count
}

// Agent-written near-miss names for the raw price indicators (the registry
// values are open/high/low/close; the model has guessed 'price' and labels)
const SIDE_NAME_ALIASES: Record<string, string> = {
  price: 'close',
  currentprice: 'close',
  closeprice: 'close',
  closecurrent: 'close',
  openprice: 'open',
  highprice: 'high',
  lowprice: 'low',
}

/**
 * Repairs an agent-written condition side toward the shape the selector UI
 * produces: indicator.name must be a registry value and field one of the
 * indicator's fields. Unresolvable names are retried lowercased and through
 * the alias map; a missing/foreign field defaults to fields[0] (exactly what
 * selecting the indicator in the UI does). MUTATES the side.
 */
const repairConditionSide = (side: any, getIndicatorByValue: IndicatorLookup): void => {
  if (!side || typeof side !== 'object' || !side.indicator || typeof side.indicator !== 'object') {
    return
  }
  const rawName = side.indicator.name
  if (typeof rawName !== 'string' || rawName === 'value') return

  let resolved = resolveSideIndicator(side, getIndicatorByValue)
  if (!resolved) {
    const lower = rawName.toLowerCase()
    const alias = SIDE_NAME_ALIASES[lower.replace(/[^a-z]/g, '')]
    resolved = getIndicatorByValue?.(lower) ?? (alias ? getIndicatorByValue?.(alias) : undefined)
    if (resolved) side.indicator.name = resolved.value
  }
  if (resolved?.fields?.length && !resolved.fields.includes(side.field)) {
    side.field = resolved.fields[0]
  }
}

const KNOWN_SETTINGS_KEYS = ['previousCandle', 'validityWindow', 'prerequisites'] as const

const sanitizeConditionSettings = (condition: any) => {
  if (!condition.settings || typeof condition.settings !== 'object') {
    delete condition.settings
    return
  }
  const settings: any = {}
  for (const key of KNOWN_SETTINGS_KEYS) {
    if (condition.settings[key] !== undefined) settings[key] = condition.settings[key]
  }
  if (settings.prerequisites && !Array.isArray(settings.prerequisites)) {
    delete settings.prerequisites
  }
  if (Object.keys(settings).length > 0) condition.settings = settings
  else delete condition.settings
}

/**
 * Turns an agent-proposed pattern draft into an editor-safe Pattern.
 *
 * The result is built as BASE-SPREAD-PLUS-ALLOWLIST: it starts from a deep
 * clone of the OPEN pattern (so identity and every field the agent doesn't
 * control — starred, versions notes, link, source, original, unknown keys —
 * survive the whole-document setDoc save path verbatim) and overwrites only
 * what the agent legitimately authors: name, message, trigger (shape-checked),
 * and the condition groups.
 *
 * Group/condition handling: ids are PRESERVED where echoed (React keys and
 * prerequisite references stay stable across turns) and minted only where
 * missing or colliding; prerequisites referencing ids that don't exist in the
 * draft are dropped; condition settings are sanitized to known keys; group
 * count is clamped to its children; chartDetails is denormalized from the
 * owning group; the shared normalize+isValid stamping is applied per
 * condition. Hard invariants (depth, node count, malformed nodes) are
 * rejected visibly, never silently "fixed".
 */
export const normalizeAgentDraft = (
  raw: unknown,
  base: Pattern,
  getIndicatorByValue: IndicatorLookup
): NormalizeDraftResult => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, reason: 'Draft is not a pattern object' }
  }
  const proposed: any = _cloneDeep(raw)

  const draft: any = {
    ..._cloneDeep(base),
    name: typeof proposed.name === 'string' && proposed.name.trim() ? proposed.name : base.name,
    message: typeof proposed.message === 'string' ? proposed.message : (base as any).message,
    // Server-built drafts (propose_pattern_edit) send chart details only; the
    // trigger TYPE is never the agent's to change, so merge over the base's
    trigger: isValidTrigger(proposed.trigger)
      ? proposed.trigger
      : isValidChartDetails(proposed.chartDetails)
        ? {
            ..._cloneDeep(base.trigger),
            chartDetails: {
              symbol: proposed.chartDetails.symbol,
              periodicity: proposed.chartDetails.periodicity,
              timeframe: proposed.chartDetails.timeframe,
            },
          }
        : _cloneDeep(base.trigger),
    buyConditionGroups: Array.isArray(proposed.buyConditionGroups)
      ? proposed.buyConditionGroups
      : [],
    sellConditionGroups: Array.isArray(proposed.sellConditionGroups)
      ? proposed.sellConditionGroups
      : [],
  }

  // Breadth cap: depth alone doesn't bound a draft; thousands of conditions
  // would freeze the editor (every added condition also auto-opens)
  const totalNodes = countNodes(draft.buyConditionGroups) + countNodes(draft.sellConditionGroups)
  if (totalNodes > MAX_DRAFT_NODES) {
    return {
      ok: false,
      reason: `Draft contains ${totalNodes} conditions/groups — the maximum is ${MAX_DRAFT_NODES}`,
    }
  }

  const warnings: string[] = []
  const seenIds = new Set<string>()

  const ensureId = (node: any) => {
    // Colliding ids: the FIRST holder keeps the id (prerequisites pointing at
    // it stay correct); later holders get fresh ids
    if (typeof node.id !== 'string' || !node.id || seenIds.has(node.id)) {
      node.id = uuidv4()
    }
    seenIds.add(node.id)
  }

  const walkGroup = (group: any, depth: number): string | null => {
    if (!group || typeof group !== 'object') return 'Draft contains a malformed condition group'
    if (depth > MAX_CONDITION_GROUP_DEPTH) {
      return `Draft nests condition groups deeper than ${MAX_CONDITION_GROUP_DEPTH} levels`
    }
    ensureId(group)
    group.type = 'group'
    group.depth = depth
    group.conditions = Array.isArray(group.conditions) ? group.conditions : []
    group.conditionGroups = Array.isArray(group.conditionGroups) ? group.conditionGroups : []
    group.groupTitle = typeof group.groupTitle === 'string' ? group.groupTitle : 'Group'
    const childCount = group.conditions.length + group.conditionGroups.length
    if (typeof group.count !== 'number' || group.count < 0 || group.count > childCount) {
      group.count = childCount
    }
    if (!group.symbol) group.symbol = base.trigger?.chartDetails?.symbol ?? 'EURUSD'
    if (!group.periodicity) group.periodicity = base.trigger?.chartDetails?.periodicity ?? 1
    if (!group.timeframe) group.timeframe = base.trigger?.chartDetails?.timeframe ?? 'minute'

    for (const condition of group.conditions) {
      if (!condition || typeof condition !== 'object' || typeof condition.condition !== 'object') {
        return 'Draft contains a malformed condition'
      }
      ensureId(condition)
      condition.type = 'condition'
      condition.sourceGroupId = group.id
      // Denormalize the group's chart onto the condition (backend requirement)
      condition.chartDetails = {
        symbol: group.symbol,
        periodicity: group.periodicity,
        timeframe: group.timeframe,
      }
      sanitizeConditionSettings(condition)
      repairConditionSide(condition.condition.LHS, getIndicatorByValue)
      repairConditionSide(condition.condition.RHS, getIndicatorByValue)
      normalizeConditionValidity(condition, getIndicatorByValue)
      if (condition.condition.isValid === false) {
        warnings.push(
          `Condition "${condition.condition?.LHS?.indicator?.name ?? '?'}" is incomplete`
        )
      }
    }
    for (const nested of group.conditionGroups) {
      const error = walkGroup(nested, depth + 1)
      if (error) return error
    }
    return null
  }

  for (const group of [...draft.buyConditionGroups, ...draft.sellConditionGroups]) {
    const error = walkGroup(group, 1)
    if (error) return { ok: false, reason: error }
  }

  // Drop prerequisites that reference ids not present in the draft — dangling
  // references would make conditions silently unsatisfiable downstream
  const filterPrereqs = (group: any) => {
    for (const condition of group.conditions) {
      const prereqs = condition.settings?.prerequisites
      if (Array.isArray(prereqs)) {
        const kept = prereqs.filter((p: any) => p && typeof p.id === 'string' && seenIds.has(p.id))
        if (kept.length > 0) condition.settings.prerequisites = kept
        else {
          delete condition.settings.prerequisites
          if (Object.keys(condition.settings).length === 0) delete condition.settings
        }
      }
    }
    group.conditionGroups.forEach(filterPrereqs)
  }
  draft.buyConditionGroups.forEach(filterPrereqs)
  draft.sellConditionGroups.forEach(filterPrereqs)

  return { ok: true, pattern: migrateLegacyChangedConditions(draft as Pattern), warnings }
}
